import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usedPromoCodes } from '@/mocks/profile';

const ProfilePromoCodes = () => {
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const totalSaved = usedPromoCodes.reduce((sum, c) => sum + c.savedAmount, 0);

  const handleCopy = (code: string, id: number) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-orbitron font-bold text-xl text-white mb-1">История промокодов</h2>
          <p className="font-mono text-xs text-white/35">Все использованные промокоды и сэкономленные суммы</p>
        </div>
        <Link
          to="/promos/search"
          className="whitespace-nowrap flex items-center gap-2 px-4 py-2.5 rounded-xl font-mono text-xs cursor-pointer transition-all self-start sm:self-auto"
          style={{ border: '1px solid rgba(255,45,120,0.35)', color: '#ff2d78', background: 'rgba(255,45,120,0.06)' }}
        >
          <i className="ri-search-line" />
          Найти акции
        </Link>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { val: usedPromoCodes.length, label: 'Использовано промокодов', color: '#ff2d78', icon: 'ri-coupon-3-line' },
          { val: `${totalSaved.toLocaleString()} ₽`, label: 'Всего сэкономлено', color: '#39ff14', icon: 'ri-money-cny-circle-line' },
          { val: `${Math.round(totalSaved / usedPromoCodes.length).toLocaleString()} ₽`, label: 'Средняя экономия', color: '#00f5ff', icon: 'ri-bar-chart-line' },
        ].map((s) => (
          <div
            key={s.label}
            className="flex items-center gap-4 p-4 rounded-2xl"
            style={{ background: `${s.color}08`, border: `1px solid ${s.color}20` }}
          >
            <div
              className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
              style={{ background: `${s.color}15`, border: `1px solid ${s.color}30` }}
            >
              <i className={`${s.icon} text-base`} style={{ color: s.color }} />
            </div>
            <div>
              <div className="font-orbitron font-black text-xl" style={{ color: s.color }}>{s.val}</div>
              <div className="font-mono text-[10px] text-white/35 leading-tight">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Codes list */}
      <div className="space-y-3">
        {usedPromoCodes.map((item) => (
          <div
            key={item.id}
            className="rounded-2xl overflow-hidden transition-all duration-300"
            style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${item.color}18` }}
          >
            {/* Top accent */}
            <div className="h-0.5" style={{ background: `linear-gradient(90deg, ${item.color}, transparent)` }} />

            <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Icon + code */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                  style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}
                >
                  <i className={`${item.icon} text-xl`} style={{ color: item.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span
                      className="font-orbitron font-black text-base tracking-widest"
                      style={{ color: item.color }}
                    >
                      {item.code}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded-full font-mono text-[9px]"
                      style={{ background: `${item.color}12`, color: item.color, border: `1px solid ${item.color}25` }}
                    >
                      {item.category}
                    </span>
                  </div>
                  <div className="font-mono text-xs text-white/55 mb-0.5">{item.title}</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-[10px] text-white/30">
                      <i className="ri-calendar-line mr-1" />
                      {item.usedAt}
                    </span>
                    <Link
                      to={`/profile`}
                      className="font-mono text-[10px] text-white/30 hover:text-white/60 transition-colors"
                    >
                      <i className="ri-shopping-bag-line mr-1" />
                      {item.orderId}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Right: discount + saved + copy */}
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                <div className="text-center">
                  <div className="font-orbitron font-black text-lg" style={{ color: item.color }}>{item.discount}</div>
                  <div className="font-mono text-[9px] text-white/30">скидка</div>
                </div>
                <div
                  className="h-8 w-px hidden sm:block"
                  style={{ background: 'rgba(255,255,255,0.08)' }}
                />
                <div className="text-center">
                  <div className="font-orbitron font-black text-lg" style={{ color: '#39ff14' }}>
                    -{item.savedAmount.toLocaleString()} ₽
                  </div>
                  <div className="font-mono text-[9px] text-white/30">сэкономлено</div>
                </div>
                <button
                  onClick={() => handleCopy(item.code, item.id)}
                  className="whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-mono text-xs cursor-pointer transition-all duration-200"
                  style={
                    copiedId === item.id
                      ? { background: '#39ff14', color: '#050508' }
                      : { border: `1px dashed ${item.color}40`, color: item.color, background: `${item.color}06` }
                  }
                >
                  <i className={copiedId === item.id ? 'ri-check-line' : 'ri-file-copy-line'} />
                  {copiedId === item.id ? 'Скопировано' : 'Копировать'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div
        className="rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
        style={{ background: 'rgba(255,45,120,0.05)', border: '1px dashed rgba(255,45,120,0.25)' }}
      >
        <div>
          <p className="font-orbitron font-bold text-sm text-white mb-1">Ищете новые акции?</p>
          <p className="font-mono text-xs text-white/40">Найдите актуальные промокоды и скидки в разделе акций</p>
        </div>
        <Link
          to="/promos"
          className="whitespace-nowrap flex items-center gap-2 px-5 py-3 rounded-xl font-orbitron font-bold text-xs cursor-pointer transition-all shrink-0"
          style={{ background: '#ff2d78', color: '#050508', boxShadow: '0 0 16px rgba(255,45,120,0.4)' }}
        >
          <i className="ri-price-tag-3-line" />
          Все акции
        </Link>
      </div>
    </div>
  );
};

export default ProfilePromoCodes;
